<template>
  <div class="quiz-form-content">
    <div class="flex flex-wrap justify-end">
      <vs-input
        class="w-full name-field"
        label="Quiz name"
        v-model="localQuiz.quiz_title"
        @blur="saveQuiz(localQuiz)"
      />
    </div>
    <div class="w-full mt-10">
      <label class="vs-input--label">Slug</label>
      <vx-input-group class="mb-base" prependClasses="border border-solid border-grey border-r-0">
        <template slot="prepend">
          <div class="prepend-text">
            <vs-switch v-model="autoAlias" :checked="autoAlias" class="auto-alias-checkbox"/>
          </div>
        </template>

        <vs-input
          v-model="localQuiz.slug" 
          :disabled="autoAlias"
          class="auto-alias-field"
          @blur="saveQuiz(localQuiz)"
        />
      </vx-input-group>
    </div>
    <div class="flex md:mt-10 mt-6" v-if="isEditing && embedded">
      <vs-input
        class="w-full id-field"
        label="UUID"
        :disabled="isEditing"
        v-model="localQuiz.id"
      />
    </div>
    <div class="flex flex-wrap justify-end flex md:mt-10 mt-6">
      <vs-input
        class="w-full video-reference-field"
        label="Video reference"
        v-model="localQuiz.video_reference"
        @blur="saveQuiz(localQuiz)"
      />
    </div>
    <div class="flex flex-wrap md:mt-10 mt-6">
      <vs-input
        class="w-full duration-field"
        label="Duration"
        v-model="duration"
        readonly
      />
    </div>
    <div class="flex mt-10">
      <div class="w-1/3 pr-2">
        <label class="vs-input--label">Time to finish</label>
        <vue-timepicker-m
          @click.native.stop=""
          class="w-full time-cap-field vs-con-input"
          v-model="timeToFinish"
          format="HH:mm:ss"
          hide-clear-button
          input-class="vs-inputx vs-input--input normal"
          input-width="100%"
        />
      </div>
      <div class="w-1/3 pr-2">
        <vs-input
          class="w-full min-score-field"
          label="Min score"
          type="tel"
          v-mask="'###'"
          v-model="percentageMinScore"
          @blur="saveQuiz(localQuiz)"
          description-text="A number between 0 and 100%. Default: 70%"
        />
      </div>
      <div class="w-1/3 pr-2">
        <vs-input
          type="number"
          class="w-full max-attempts-field"
          label="Max attempts"
          v-model="localQuiz.max_attempts_per_user"
          @blur="saveQuiz(localQuiz)"
          description-text="Leave blank to set unlimited attempts"
        />
      </div>
      
    </div>
    <div class="flex w-full mt-10">
      <div class="w-1/3 flex">
        <vs-switch @blur="saveQuiz(localQuiz)" label="shufle" v-model="localQuiz.shuffle_questions" :checked="localQuiz.shuffle_questions" class="mr-3 shuffle-questions" />
        <label>Shuffle questions</label>
      </div>
    </div>
    <div class="flex w-full mt-10">
        
      </div>
    <div class="mt-10 quiz-tree" v-if="isEditing">
      <h3>Quiz structure</h3>
      <div class="questions-list" v-if="localQuestions">
        <div class="mt-10" v-for="(question, qIndex) in localQuestions" :key="question.id || qIndex">
          <question-content
            :question="question"
            :index="qIndex"
            :answers="localAnswers"
            :finishEditQuestion="finishEditQuestion"
            :deleteQuestion="deleteQuestion"
            :addAnswer="addAnswer"
            @finish-edit-answer="finishEditAnswer"
            @delete-answer="deleteAnswer"
          />
        </div>
      </div>
      <div class="add-question-btn flex text-lg mt-10 cursor-pointer" @click.stop="addQuestion()"><feather-icon icon="PlusCircleIcon" svgClasses="h-6 w-6" class="mr-4" />
        Add question
      </div>
    </div>
    <div class="flex md:mt-10 mt-6 action-buttons" v-if="embedded">
      <vs-button class="w-full save-button" color="primary" icon-pack="feather" @click.stop="saveAndClick">Save</vs-button>
    </div>
  </div>
</template>
<script>
import slug from 'slug-generator'
import uuid from 'uuid'
import cloneDeep from 'lodash.clonedeep'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { questionFactory, answerFactory } from '@/store/quiz/factories.js'
import { mask } from 'vue-the-mask'
import { HmsToSeconds, secondsToHms } from '@/utils/utils.js'
import VueTimepickerM from 'vue2-timepicker'
import 'vue2-timepicker/dist/VueTimepicker.css'
import QuestionContent from './QuestionContent'

export default {
  components: { QuestionContent, VueTimepickerM },
  directives: {
    mask
  },
  props: {
    embedded: Boolean,
    onClick: {
      type: Function,
      required: true
    },
  },
  data () {
    return {
      autoAlias: true,
      localQuiz: {},
      localQuestions: {},
      localAnswers: {},
      timeToFinish: '00:00:00',
      timeToFinishInSeconds: '0',
      duration: '00:00:00',
      durationInSeconds: '0',
    }
  },
  watch: {
    'localQuiz': {
      deep: true,
      handler (newValue) {
        if (this.autoAlias) {
          this.localQuiz.slug = slug(newValue.quiz_title)
        }
      }
    },
    'autoAlias': function (newValue) {
      if (newValue) {
        this.localQuiz.slug = slug(this.localQuiz.quiz_title)
      }
    },
    'getCurrentQuestions': function () {
      this.localQuestions = cloneDeep(this.getCurrentQuestions) || {}
    },
    'getCurrentAnswers': function () {
      this.localAnswers = cloneDeep(this.getCurrentAnswers) || {}
      this.localQuestions = cloneDeep(this.getCurrentQuestions) || {}
    },
    'getCurrentQuiz': function () {
      this.localQuiz = cloneDeep(this.getCurrentQuiz)
      this.localQuestions = cloneDeep(this.getCurrentQuestions) || {}
      this.localAnswers = cloneDeep(this.getCurrentAnswers) || {}
      this.timeToFinishInSeconds = this.localQuiz.time_cap_in_seconds
      this.timeToFinish = secondsToHms(this.timeToFinishInSeconds)
      this.durationInSeconds = this.localQuiz.duration_in_seconds
      this.duration = secondsToHms(this.durationInSeconds)
    },
    'timeToFinish': function() {
      this.timeToFinishInSeconds = HmsToSeconds(this.timeToFinish)
      this.transformTimeToFinishAndSaveQuiz()
    },
    'duration': function (newValue) {
      this.durationInSeconds = HmsToSeconds(newValue)
      this.transformDurationAndSaveQuiz()
    },
  },
  
  computed: {
    ...mapGetters('quizzes', [
      'getCurrentQuiz',
      'getCurrentQuestions',
      'getCurrentAnswers'
    ]),
    isEditing () {
      return !!this.getCurrentQuiz.id
    },
    percentageMinScore: {
      get() {
        return this.localQuiz.min_score * 100
      },
      set(value) {
        if (value > 100) value = 100
        if (value < 0) value = 0
        this.localQuiz.min_score = value / 100
      }
    }
  },
  methods: {
    ...mapActions('quizzes', [
      'createQuestion',
      'editQuestion',
      'deleteQuestionAction',
      'createAnswer',
      'editAnswer',
      'deleteAnswerAction'
    ]),
    ...mapMutations('quizzes', {
      saveQuiz: 'STORE_CURRENT_QUIZ',
      saveQuestions: 'STORE_CURRENT_QUESTIONS',
      saveAnswers: 'STORE_CURRENT_ANSWERS'}),
    saveAndClick() {
      this.saveQuiz(this.localQuiz)
      this.onClick()
    },
    addQuestion () {
      const index = uuid.v4()
      this.$set(this.localQuestions, index, {...questionFactory()})
    },
    async finishEditQuestion (question, index) {
      this.$vs.loading()
      try {
        if (this.getCurrentQuestions && this.getCurrentQuestions.hasOwnProperty(index)) {
          //need to edit
          await this.editQuestion({
            question: { ...question },
            index: index
          })
        } else {
          // need to create
          await this.createQuestion({
            question: { ...question },
            index: index
          })
        }
        this.saveToStore()
        this.$vs.loading.close()
      } catch (error) {
        this.$vs.loading.close()
        this.errorHandler(error)
        return false
      }
      
      return true
    },
    async finishEditAnswer ({answer, index, questionId}) {
      this.$vs.loading()
      try {
        if (this.getCurrentAnswers && this.getCurrentAnswers.hasOwnProperty(index)) {
          //need to edit
          await this.editAnswer({
            answer: { ...answer },
            answerId: index,
            questionId: questionId
          })
        } else {
          // need to create
          await this.createAnswer({
            answer: { ...answer },
            index: index,
            questionId: questionId,
            addFunc: this.$set
          })
        }
        this.saveToStore()
      } catch (error) {
        this.errorHandler(error)
      }
      this.$vs.loading.close()
    },
    addAnswer (qId) {
      const index = uuid.v4()
      this.$set(this.localAnswers, index, {...answerFactory()})
      this.localQuestions[qId].answers.push(index)
    },
    async deleteQuestion (qId) {
      this.$vs.loading()
      try {
        await this.deleteQuestionAction({
          index: qId,
          deleteFunc: this.$delete
        })
        this.saveToStore()
      } catch (error) {
        this.errorHandler(error)
      }
      this.$vs.loading.close()
    },
    async deleteAnswer ({questionId, answerId}) {
      this.$vs.loading()
      try {
        await this.deleteAnswerAction({
          answerId: answerId,
          questionId: questionId,
          deleteFunc: this.$delete
        })
        this.saveToStore()
      } catch (error) {
        this.errorHandler(error)
      }
      this.$vs.loading.close()
    },
    saveToStore () {
      this.saveQuestions(this.localQuestions)
      this.saveAnswers(this.localAnswers)
    },
    errorHandler (error) {
      this.showError(error)
    },
    transformTimeToFinishAndSaveQuiz() {
      this.timeToFinishInSeconds = HmsToSeconds(this.timeToFinish)
      this.localQuiz.time_cap_in_seconds = this.timeToFinishInSeconds
      this.saveQuiz(this.localQuiz)
    },
    transformDurationAndSaveQuiz() {
      this.localQuiz.duration_in_seconds = this.durationInSeconds
      this.saveQuiz(this.localQuiz)
    },
  },
  mounted () {
    this.localQuiz = cloneDeep(this.getCurrentQuiz)
    this.localQuestions = cloneDeep(this.getCurrentQuestions)
    this.localAnswers = cloneDeep(this.getCurrentAnswers)
    this.timeToFinishInSeconds = this.localQuiz.time_cap_in_seconds
    this.timeToFinish = secondsToHms(this.timeToFinishInSeconds)
    this.durationInSeconds = this.localQuiz.duration_in_seconds
    this.duration = secondsToHms(this.durationInSeconds)
  },
}
</script>